import cloneDeep from 'lodash/cloneDeep.js'
import isEqual from 'lodash/isEqual.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../../store.js'
import ConfirmModal from '../../../../common/components/Modal/ConfirmModal.js'
import Currency from '../../../../common/components/Currency.js'
import {formatWeight, splitOz} from '../../../../common/weight.js'
import {showMessageToast} from '../../../Header/Toast/index.js'
import {
  updateLabelConfig,
  addCustomsLine,
  labelConfigSelector,
  customsInfoSelector,
  totalQuantitySelector,
  totalValueSelector,
  totalWeightSelector,
  generalCustomsErrorsSelector,
  canAddLineSelector,
  labelShipFromAddressSelector,
  labelShipToAddressSelector,
  customsErrorsSelector,
  getCanHaveHarmonizationCode,
  getCanHaveHarmonizationCodeCountry,
  getCanHaveSKU,
  getCanHaveEUPIDs,
  unifiedOptionsSelector,
  shipperTypesSelector,
  LABEL_PROPERTY_SEND_EU_PRODUCT_IDENTIFIERS,
} from '../../../../data/labelInfos/index.js'
import Line from './Line.js'
import {useMemo} from 'react'
import Checkbox from '../../../../common/components/Checkbox.js'

export const MODAL_FORM = 'customs_modal'

export function showCustomsModal(labelInfoID) {
  const savedConfig = cloneDeep(labelConfigSelector(getState(), {labelInfoID}))

  setForm(MODAL_FORM, {
    labelInfoID,
    savedConfig,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  const forms = formsSelector(state)

  return forms[MODAL_FORM]
}

export async function confirm() {
  const {labelInfoID, savedConfig} = modalFormSelector(getState())
  const labelConfig = labelConfigSelector(getState(), {labelInfoID})

  closeModal()

  if (!isEqual(savedConfig, labelConfig)) {
    showMessageToast('Customs declarations were successfully updated.')
  }
}

export async function cancel() {
  const form = modalFormSelector(getState())

  if (!form) {
    return
  }

  const {labelInfoID, savedConfig} = form

  closeModal()

  updateLabelConfig(labelInfoID, savedConfig)
}

function CustomsModal({form}) {
  const {labelInfoID} = form

  const shipperTypes = useSelector((state) =>
    shipperTypesSelector(state, {labelInfoID}),
  )
  const lines = useSelector((state) =>
    customsInfoSelector(state, {labelInfoID}),
  )
  const totalQuantity = useSelector((state) =>
    totalQuantitySelector(state, {labelInfoID}),
  )
  const totalValue = useSelector((state) =>
    totalValueSelector(state, {labelInfoID}),
  )
  const totalWeight = useSelector((state) =>
    totalWeightSelector(state, {labelInfoID}),
  )
  const hasErrors = !!useSelector((state) =>
    customsErrorsSelector(state, {labelInfoID}),
  )
  const generalCustomsErrors = useSelector((state) =>
    generalCustomsErrorsSelector(state, {labelInfoID}),
  )
  const canAddLine = useSelector((state) =>
    canAddLineSelector(state, {labelInfoID}),
  )
  const unitOfMeasureOptions = useSelector((state) =>
    unifiedOptionsSelector(state, {
      labelInfoID,
      labelProperty: 'unit_of_measure',
    }),
  )
  const {
    [LABEL_PROPERTY_SEND_EU_PRODUCT_IDENTIFIERS]: send_eu_product_identifiers,
  } = useSelector((state) => labelConfigSelector(state, {labelInfoID}))
  const [
    canHaveHarmonizationCode,
    canHaveHarmonizationCodeCountry,
    canHaveSKU,
    canHaveEUPIDs,
  ] = useMemo(
    () => [
      shipperTypes.some((shipperType) =>
        getCanHaveHarmonizationCode(shipperType),
      ),
      shipperTypes.some((shipperType) =>
        getCanHaveHarmonizationCodeCountry(shipperType),
      ),
      shipperTypes.some((shipperType) => getCanHaveSKU(shipperType)),
      shipperTypes.some((shipperType) => getCanHaveEUPIDs(shipperType)),
    ],
    [shipperTypes],
  )
  const shipFromAddress = useSelector((state) =>
    labelShipFromAddressSelector(state, {labelInfoID}),
  )
  const shipToAddress = useSelector((state) =>
    labelShipToAddressSelector(state, {labelInfoID}),
  )
  const lastCustomLine = lines[lines.length - 1] || {
    description: '',
    quantity: 1,
    value: 0,
    weightLB: 0,
    weightOZ: 0,
    sku: '',
    country: shipFromAddress.country || 'US',
    harmonizationCode: '',
    harmonizationCodeCountry: shipToAddress.country || 'US',
    m_pid: '',
    ns_pid: '',
    s_pid: '',
  }
  const showEUPIDs = send_eu_product_identifiers && canHaveEUPIDs

  return (
    <ConfirmModal
      title="Edit Customs Declarations"
      modalSize="xx-lg"
      onConfirm={() => confirm()}
      onCancel={() => cancel()}
      confirmText="Save and Return"
      cancelText="Cancel"
      isDisabled={hasErrors}
    >
      <div className="row">
        <div className="medium-12 columns">
          {generalCustomsErrors.map((errorMessage) => (
            <dl
              key={errorMessage}
              className="list list--error margin-bottom-10"
            >
              <dd className="fs-n0 margin-bottom-0">{errorMessage}</dd>
            </dl>
          ))}
          {canHaveEUPIDs && (
            <div className="margin-bottom-10">
              <Checkbox
                id="customs__send_eu_product_identifiers"
                label="Send EU product identifiers"
                checked={!!send_eu_product_identifiers}
                onChange={(send_eu_product_identifiers) =>
                  updateLabelConfig(labelInfoID, {send_eu_product_identifiers})
                }
              />
            </div>
          )}
          <table className="table table--customs-declarations">
            <thead>
              <tr>
                <th className="table__th table__th--sm th-width-15">
                  Desc of Contents
                </th>
                <th className="table__th table__th--sm th-width-5">Qty</th>
                {unitOfMeasureOptions.length > 0 && (
                  <th
                    className="table__th table__th--sm th-width-10"
                    title="Unit of Measure"
                  >
                    Unit of Measure
                  </th>
                )}
                <th
                  className="table__th table__th--sm th-width-10"
                  title="Total Weight"
                >
                  Total Weight
                </th>
                <th className="table__th table__th--sm th-width-10">
                  Unit Value
                </th>
                {canHaveSKU && (
                  <th className="table__th table__th--sm th-width-10">SKU</th>
                )}
                {canHaveHarmonizationCode && (
                  <th
                    className="table__th table__th--sm th-width-10"
                    title="Harmonized System Code"
                  >
                    HS Code
                  </th>
                )}
                {canHaveHarmonizationCodeCountry && (
                  <th
                    className="table__th table__th--sm"
                    title="Destination Country"
                  >
                    Dest. Ctry
                  </th>
                )}
                {showEUPIDs && (
                  <>
                    <th className="table__th table__th--sm">EU M-PID</th>
                    <th className="table__th table__th--sm">EU NS-PID</th>
                    <th className="table__th table__th--sm">EU S-PID</th>
                  </>
                )}
                <th
                  className="table__th table__th--sm th-width-5"
                  title="Country of Origin"
                >
                  Ctry of Origin
                </th>
                <th className="table__th table__th--sm th-width-3">&nbsp;</th>
              </tr>
            </thead>
            <tbody className="table__tbody table__tbody--lines">
              {lines.map((line, index) => (
                <Line
                  key={index}
                  index={index}
                  labelInfoID={labelInfoID}
                  customsInfoIndex={index}
                  canHaveHarmonizationCode={canHaveHarmonizationCode}
                  canHaveHarmonizationCodeCountry={
                    canHaveHarmonizationCodeCountry
                  }
                  canHaveSKU={canHaveSKU}
                  showEUPIDs={showEUPIDs}
                  unitOfMeasureOptions={unitOfMeasureOptions}
                />
              ))}
            </tbody>
            <tbody className="table__tbody table__tbody--single-total">
              <tr>
                <td className="table__td">
                  <strong className="fs-n1">Totals</strong>
                </td>
                <td className="table__td border-left--light">
                  <strong className="fs-n1">{totalQuantity}</strong>
                </td>
                {unitOfMeasureOptions.length > 0 && (
                  <td className="table__td border-left--light"></td>
                )}
                <td className="table__td border-left--light">
                  <strong className="fs-n1">
                    {formatWeight(splitOz(totalWeight))}
                  </strong>
                </td>
                <td className="table__td border-left--light">
                  <strong className="fs-n1">
                    <Currency value={totalValue} />
                  </strong>
                </td>
                <td className="table__td border-left--light" />
                {canHaveSKU && <td className="table__td" />}
                {canHaveHarmonizationCode && <td className="table__td" />}
                {canHaveHarmonizationCodeCountry && (
                  <td className="table__td" />
                )}
                {showEUPIDs && <td className="table__td" colSpan="3" />}
                <td className="table__td" />
              </tr>
            </tbody>
            {canAddLine && (
              <tbody>
                <tr>
                  <td
                    className="table__td table__td--product-search align-center"
                    colSpan={
                      (canHaveSKU ? 1 : 0) +
                      (canHaveHarmonizationCode ? 1 : 0) +
                      (canHaveHarmonizationCodeCountry ? 1 : 0) +
                      (showEUPIDs ? 3 : 0) +
                      6
                    }
                  >
                    <button
                      className="btn btn--link fs-n0"
                      onClick={() =>
                        addCustomsLine(labelInfoID, {...lastCustomLine})
                      }
                    >
                      Add Another Line
                    </button>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>
    </ConfirmModal>
  )
}

export default onlyIfForm(CustomsModal, modalFormSelector)
